/**
 * Obligations: every dated thing the company owes. Each has a date rule rather
 * than a typed date:
 *
 * - once, on a date (or "to confirm" until there is one);
 * - repeating every so many weeks, months or years, counted from when each was
 *   due or from when the last one was done;
 * - after each end of a period: the financial year end, a VAT quarter, a PAYE
 *   period, or the incorporation anniversary. Statutory deadlines are these:
 *   accounts 9 months after the year end, corporation tax 9 months and a day,
 *   the CT600 12 months, VAT 1 month and 7 days after each quarter, PAYE on the
 *   22nd, the confirmation statement 14 days after the anniversary.
 *
 * Period ends come from the company's dates in Admin and the financial year
 * in finance settings. Completing an occurrence keeps it, with its evidence
 * and whether it was on time; the next one follows from the rule.
 */
import { addDays, addMonths, daysInMonth, endOfMonth, isoOf, type ISODate } from "./dates";

export type RepeatUnit = "week" | "month" | "year";
export type PeriodKind = "year_end" | "vat_quarter" | "paye_period" | "incorporation";
export const PERIOD_LABEL: Record<PeriodKind, string> = { year_end: "the year end", vat_quarter: "each VAT quarter", paye_period: "each PAYE period", incorporation: "the incorporation anniversary" };

export type DateRule =
  | { kind: "once"; date: ISODate | null }
  | {
      kind: "repeat";
      every: number;
      unit: RepeatUnit;
      /** The first one. */
      from: ISODate;
      /** Counted from when each was due, or from when the last was done. */
      basis: "due" | "done";
    }
  | {
      kind: "after";
      period: PeriodKind;
      months: number;
      days: number;
      /** On this day of the month instead (PAYE: the 22nd), `months` after the period ends. */
      dayOfMonth: number | null;
      /** Companies House's first accounts: a first period over 12 months is due 21 months after incorporation (or 3 months after it ends, if later). */
      firstAccounts?: boolean;
    };

export interface CompanyDates {
  incorporatedOn: ISODate | null;
  /** The month (1–3) the first VAT quarter of the calendar year ends: 1 for Jan/Apr/Jul/Oct, 3 for Mar/Jun/Sep/Dec. Null: not VAT registered. */
  vatQuarterEnds: 1 | 2 | 3 | null;
  payeFrequency: "monthly" | "quarterly" | null;
}
export const DEFAULT_COMPANY: CompanyDates = { incorporatedOn: null, vatQuarterEnds: null, payeFrequency: null };

export interface ObligationCategory {
  id: string;
  name: string;
  active: boolean;
  sortOrder: number;
}

export interface ObligationCompletion {
  id: string;
  /** The occurrence it completed. */
  dueOn: ISODate;
  doneOn: ISODate;
  /** Not needed this time, with the reason in the note. */
  skipped: boolean;
  note: string | null;
  link: string | null;
  by: string | null;
  at: string;
}

export interface Obligation {
  id: string;
  ref: number;
  title: string;
  notes: string;
  categoryId: string | null;
  ownerId: string | null;
  /** Days ahead it counts as due soon. */
  leadDays: number;
  rule: DateRule;
  /** Occurrences due before this aren't owed here (tracking started then). */
  startsOn: ISODate;
  /** One of the statutory set, added from Admin. */
  preset: StatutoryPreset | null;
  /** The finance line that pays it (PAYE, VAT, corporation tax). */
  commitmentId: string | null;
  /** Risks it guards against. */
  riskIds: string[];
  active: boolean;
  completions: ObligationCompletion[];
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export const obligationRef = (n: number) => `O-${String(n).padStart(3, "0")}`;

/* ------------------------------------------------------------------ */
/* Dates                                                               */
/* ------------------------------------------------------------------ */

const isMonthEnd = (d: ISODate) => endOfMonth(d) === d;
/** n months on; a month end stays a month end (30 June → 31 March). */
const monthsOn = (d: ISODate, n: number) => (isMonthEnd(d) ? endOfMonth(addMonths(d, n, 1)) : addMonths(d, n));

export interface PeriodContext {
  company: CompanyDates;
  /** 1–12, from finance settings. */
  yearStartMonth: number;
}

/** Why a rule can't give dates yet: a company date it needs isn't set. */
export function missingFor(rule: DateRule, ctx: PeriodContext): string | null {
  if (rule.kind !== "after") return null;
  if (rule.period === "vat_quarter" && !ctx.company.vatQuarterEnds) return "VAT quarters";
  if (rule.period === "paye_period" && !ctx.company.payeFrequency) return "PAYE frequency";
  if (rule.period === "incorporation" && !ctx.company.incorporatedOn) return "incorporation date";
  return null;
}

/** The ends of a kind of period between two dates, oldest first. */
export function periodEnds(kind: PeriodKind, ctx: PeriodContext, from: ISODate, to: ISODate): ISODate[] {
  const out: ISODate[] = [];
  const y0 = Number(from.slice(0, 4)) - 1;
  const y1 = Number(to.slice(0, 4)) + 1;
  const push = (d: ISODate) => d >= from && d <= to && out.push(d);
  const endMonth = ctx.yearStartMonth === 1 ? 12 : ctx.yearStartMonth - 1;
  if (kind === "year_end") for (let y = y0; y <= y1; y++) push(endOfMonth(isoOf(y, endMonth, 1)));
  if (kind === "vat_quarter" && ctx.company.vatQuarterEnds) for (let y = y0; y <= y1; y++) for (let q = 0; q < 4; q++) push(endOfMonth(isoOf(y, ctx.company.vatQuarterEnds + q * 3, 1)));
  if (kind === "paye_period" && ctx.company.payeFrequency)
    for (let y = y0; y <= y1; y++) for (let m = 1; m <= 12; m++) if (ctx.company.payeFrequency === "monthly" || [1, 4, 7, 10].includes(m)) push(isoOf(y, m, 5));
  if (kind === "incorporation" && ctx.company.incorporatedOn) {
    const [, mm, dd] = ctx.company.incorporatedOn.split("-").map(Number);
    for (let y = y0; y <= y1; y++) push(isoOf(y, mm!, Math.min(dd!, daysInMonth(y, mm!))));
  }
  return [...new Set(out)].sort();
}

/** When an occurrence is due, for the period ending on a date. */
export function dueAfter(rule: Extract<DateRule, { kind: "after" }>, periodEnd: ISODate): ISODate {
  if (rule.dayOfMonth) return addMonths(isoOf(Number(periodEnd.slice(0, 4)), Number(periodEnd.slice(5, 7)), 1), rule.months, rule.dayOfMonth);
  return addDays(monthsOn(periodEnd, rule.months), rule.days);
}

/** The accounts' deadline: a first period of more than 12 months is due 21 months after incorporation, or 3 months after it ends if later. */
function firstAccountsDue(rule: Extract<DateRule, { kind: "after" }>, periodEnd: ISODate, ctx: PeriodContext): ISODate {
  const inc = ctx.company.incorporatedOn;
  const normal = dueAfter(rule, periodEnd);
  if (!inc || periodEnd <= addMonths(inc, 12)) return normal;
  // only the first period: the first year end a year or more after incorporation
  if (periodEnds("year_end", ctx, addMonths(inc, 12), addDays(periodEnd, -1)).length) return normal;
  const a = addMonths(inc, 21);
  const b = monthsOn(periodEnd, 3);
  return a > b ? a : b;
}

/** Every occurrence a rule gives between two dates (repeats from when done aren't known ahead: only the next). */
export function occurrencesOf(rule: DateRule, ctx: PeriodContext, from: ISODate, to: ISODate): ISODate[] {
  if (rule.kind === "once") return rule.date && rule.date >= from && rule.date <= to ? [rule.date] : [];
  if (rule.kind === "repeat") {
    const out: ISODate[] = [];
    for (let k = 0; k < 2000; k++) {
      const d = rule.unit === "week" ? addDays(rule.from, 7 * rule.every * k) : monthsOn(rule.from, (rule.unit === "year" ? 12 : 1) * rule.every * k);
      if (d > to) break;
      if (d >= from) out.push(d);
    }
    return out;
  }
  if (missingFor(rule, ctx)) return [];
  // a period ends up to a year and a bit (or 21 months, for long first accounts) before its deadline
  const inc = ctx.company.incorporatedOn;
  return periodEnds(rule.period, ctx, addMonths(from, -(rule.months + 22)), to)
    // nothing before the company existed; the first accounts run to the year end after the first anniversary
    .filter((e) => rule.period !== "year_end" || !inc || (rule.firstAccounts ? e >= addMonths(inc, 12) : e > inc))
    .map((e) => (rule.firstAccounts ? firstAccountsDue(rule, e, ctx) : dueAfter(rule, e)))
    .filter((d) => d >= from && d <= to);
}

export type ObligationState = "overdue" | "due_soon" | "to_confirm" | "upcoming" | "done";
export const OBLIGATION_STATE_LABEL: Record<ObligationState, string> = { overdue: "Overdue", due_soon: "Due soon", to_confirm: "To confirm", upcoming: "Upcoming", done: "Done" };

export interface ObligationNow {
  /** The occurrence owed now; null if there's no date yet, or it's done for good. */
  due: ISODate | null;
  state: ObligationState;
  /** A company date the rule needs, if it's missing. */
  missing: string | null;
}

/** What's owed now: the earliest occurrence, from when tracking started, not yet completed. */
export function obligationNow(o: Pick<Obligation, "rule" | "startsOn" | "leadDays" | "completions" | "active">, ctx: PeriodContext, today: ISODate): ObligationNow {
  const done = new Set(o.completions.map((c) => c.dueOn));
  const state = (due: ISODate | null): ObligationState => (!due ? "to_confirm" : due < today ? "overdue" : due <= addDays(today, o.leadDays) ? "due_soon" : "upcoming");
  const rule = o.rule;
  if (rule.kind === "once") {
    if (o.completions.length) return { due: null, state: "done", missing: null };
    return { due: rule.date, state: state(rule.date), missing: null };
  }
  if (rule.kind === "repeat" && rule.basis === "done") {
    const last = [...o.completions].sort((a, b) => b.doneOn.localeCompare(a.doneOn))[0];
    const due = last ? (rule.unit === "week" ? addDays(last.doneOn, 7 * rule.every) : monthsOn(last.doneOn, (rule.unit === "year" ? 12 : 1) * rule.every)) : rule.from;
    return { due, state: state(due), missing: null };
  }
  const missing = missingFor(rule, ctx);
  if (missing) return { due: null, state: "to_confirm", missing };
  const due = occurrencesOf(rule, ctx, o.startsOn, addDays(today, 3 * 366)).find((d) => !done.has(d)) ?? null;
  return { due, state: state(due), missing: null };
}

/** The next few due dates, for showing what a rule means. */
export function upcomingDates(o: Pick<Obligation, "rule" | "startsOn" | "completions">, ctx: PeriodContext, today: ISODate, count = 4): ISODate[] {
  if (o.rule.kind === "repeat" && o.rule.basis === "done") {
    const now = obligationNow({ ...o, leadDays: 0, active: true }, ctx, today);
    return now.due ? [now.due] : [];
  }
  const done = new Set(o.completions.map((c) => c.dueOn));
  return occurrencesOf(o.rule, ctx, o.startsOn, addDays(today, 5 * 366))
    .filter((d) => !done.has(d))
    .slice(0, count);
}

export const onTime = (c: Pick<ObligationCompletion, "doneOn" | "dueOn" | "skipped">) => c.skipped || c.doneOn <= c.dueOn;

/** "3 of 4 on time". */
export function trackRecord(completions: ObligationCompletion[]) {
  const counted = completions.filter((c) => !c.skipped);
  return { total: counted.length, onTime: counted.filter(onTime).length, skipped: completions.length - counted.length };
}

/** What a rule means, in words. */
export function ruleLabel(rule: DateRule): string {
  if (rule.kind === "once") return "Once";
  if (rule.kind === "repeat") {
    const unit = rule.every === 1 ? rule.unit : `${rule.every} ${rule.unit}s`;
    return `Every ${unit}${rule.basis === "done" ? ", from when last done" : ""}`;
  }
  const months = rule.months ? `${rule.months} ${rule.months === 1 ? "month" : "months"}` : "";
  if (rule.dayOfMonth) return `On the ${ordinal(rule.dayOfMonth)}${months ? `, ${months}` : ""} after ${PERIOD_LABEL[rule.period]}`;
  const parts = [months, rule.days ? `${rule.days} ${rule.days === 1 ? "day" : "days"}` : ""].filter(Boolean).join(" and ");
  return parts ? `${parts} after ${PERIOD_LABEL[rule.period]}` : `On ${PERIOD_LABEL[rule.period]}`;
}

const ordinal = (n: number) => `${n}${n % 10 === 1 && n !== 11 ? "st" : n % 10 === 2 && n !== 12 ? "nd" : n % 10 === 3 && n !== 13 ? "rd" : "th"}`;

/* ------------------------------------------------------------------ */
/* The statutory set                                                   */
/* ------------------------------------------------------------------ */

export type StatutoryPreset = "accounts" | "ct_payment" | "ct600" | "confirmation" | "vat" | "paye";

export const STATUTORY: Record<StatutoryPreset, { title: string; category: string; leadDays: number; rule: Extract<DateRule, { kind: "after" }> }> = {
  accounts: { title: "Annual accounts to Companies House", category: "Statutory filing", leadDays: 60, rule: { kind: "after", period: "year_end", months: 9, days: 0, dayOfMonth: null, firstAccounts: true } },
  ct_payment: { title: "Corporation tax payment", category: "Tax", leadDays: 30, rule: { kind: "after", period: "year_end", months: 9, days: 1, dayOfMonth: null } },
  ct600: { title: "Corporation tax return (CT600)", category: "Tax", leadDays: 60, rule: { kind: "after", period: "year_end", months: 12, days: 0, dayOfMonth: null } },
  // the statement date is the day before the anniversary; it's due 14 days after that
  confirmation: { title: "Confirmation statement", category: "Statutory filing", leadDays: 14, rule: { kind: "after", period: "incorporation", months: 0, days: 13, dayOfMonth: null } },
  vat: { title: "VAT return and payment", category: "Tax", leadDays: 21, rule: { kind: "after", period: "vat_quarter", months: 1, days: 7, dayOfMonth: null } },
  paye: { title: "PAYE and NI payment", category: "Tax", leadDays: 7, rule: { kind: "after", period: "paye_period", months: 0, days: 0, dayOfMonth: 22 } },
};
